import React, { useState, useEffect } from "react";
import api from "../../api/api";
import PlaceOfBirthField from "../../components/PlaceOfBirthField";
import "../../styles/profile.css";

const initialProfile = {
  full_name: "",
  dob: "",
  birth_time: "",
  birth_place: "",
  gender: "",
  mobile: "",
};

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function calculateAge(dateStr) {
  if (!dateStr) return "";
  const today = new Date();
  const birthDate = new Date(dateStr);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

const ProfileList = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(initialProfile);
  const [formMode, setFormMode] = useState("create"); // 'create' or 'edit'
  const [editId, setEditId] = useState(null);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const userId = JSON.parse(localStorage.getItem("user"))?.userid;

  const fetchProfiles = async () => {
    setListLoading(true);
    try {
      const res = await api.get("/profile/user/" + userId);
      if (res.data && Array.isArray(res.data.data)) {
        setProfiles(res.data.data);
      } else {
        setProfiles([]);
      }
    } catch (err) {
      setProfiles([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  const handleInput = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError("");
    setFormSuccess("");
  };

  const handlePobChange = (val) => {
    setForm({ ...form, birth_place: val });
    setFormError("");
    setFormSuccess("");
  };

  function toDateInputValue(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    // Adjust for timezone offset
    const off = d.getTimezoneOffset();
    d.setMinutes(d.getMinutes() - off);
    return d.toISOString().slice(0, 10);
  }

  const handleEdit = (profile) => {
    console.log("Edit profile:", profile);
    setForm({
      full_name: profile.fullname || profile.name || "",
      dob: toDateInputValue(profile.dob) || "",
      birth_time: profile.birth_time || profile.tob || "",
      birth_place: profile.birth_place || profile.pob || "",
      gender: profile.gender || "",
      mobile: profile.mobile || "",
    });
    setEditId(profile.userdata_id);
    setFormMode("edit");
    setFormError("");
    setFormSuccess("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this profile?")) return;
    setListLoading(true);
    try {
      await api.delete(`/profile/${id}`);
      setProfiles((prev) => prev.filter((p) => p.id !== id));
      setFormSuccess("Profile deleted.");
      if (editId === id) {
        setForm(initialProfile);
        setFormMode("create");
        setEditId(null);
      }
    } catch (err) {
      setFormError("Failed to delete profile.");
    } finally {
      setListLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");
    if (
      !form.full_name.trim() ||
      !form.dob ||
      !form.birth_time ||
      !form.birth_place ||
      !form.gender ||
      !form.mobile
    ) {
      setFormError("All required fields must be filled.");
      return;
    }
    
    // Validate mobile number
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(form.mobile.replace(/\D/g, ''))) {
      setFormError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setLoading(true);
    try {
      const profileData = {
        ...form,
        user_id: userId // Include user_id for create operations
      };
      
      if (formMode === "create") {
        await api.post("/profile", profileData);
        setFormSuccess("✅ Profile created successfully!");
      } else {
        await api.put(`/profile/${editId}`, form);
        setFormSuccess("✅ Profile updated successfully!");
      }
      setForm(initialProfile);
      setFormMode("create");
      setEditId(null);
      fetchProfiles();
    } catch (err) {
      setFormError(`Failed to ${formMode === "create" ? "create" : "update"} profile. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleNewProfile = () => {
    setForm(initialProfile);
    setFormMode("create");
    setEditId(null);
    setFormError("");
    setFormSuccess("");
  };

  return (
    <div className="modern-profile-page">
      <div className="modern-profile-container">
        {/* Header Section */}
        <div className="profile-page-header">
          <div className="profile-header-content">
            <h1 className="profile-page-title">
              <span className="profile-icon">👤</span>
              Profile Management
            </h1>
            <p className="profile-page-subtitle">
              Create and manage astrology profiles for accurate readings
            </p>
          </div>
        </div>

        <div className="profile-content-grid">
          {/* Profile List Section */}
          <div className="profile-list-card">
            <div className="profile-list-header">
              <h3 className="profile-list-title">
                <span className="list-icon">📝</span>
                Saved Profiles ({profiles.length})
              </h3>
              <button
                className="new-profile-btn"
                onClick={handleNewProfile}
                disabled={loading}
              >
                <span className="btn-icon">➕</span>
                New Profile
              </button>
            </div>
            
            <div className="profile-list-content">
              {listLoading ? (
                <div className="profile-list-loading">
                  <div className="spinner"></div>
                  <span>Loading profiles...</span>
                </div>
              ) : profiles.length === 0 ? (
                <div className="profile-list-empty">
                  <div className="empty-icon">📋</div>
                  <h4>No profiles found</h4>
                  <p>Create your first profile to get started</p>
                </div>
              ) : (
                <div className="profile-list">
                  {profiles.map((profile, idx) => (
                    <div
                      className={`modern-profile-item ${editId === profile.userdata_id ? 'editing' : ''}`}
                      key={profile.userdata_id || profile.full_name || profile.mobile || idx}
                    >
                      <div className="profile-item-header">
                        <div className="profile-basic-info">
                          <h4 className="profile-name">{profile.fullname}</h4>
                          <div className="profile-details">
                            <span className="profile-age">
                              {calculateAge(profile.dob)} years
                            </span>
                            {profile.gender && (
                              <span className={`profile-gender gender-${profile.gender.toLowerCase()}`}>
                                {profile.gender === 'Male' ? '♂️' : profile.gender === 'Female' ? '♀️' : '⚧️'} {profile.gender}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="profile-actions">
                          <button
                            className="profile-edit-btn"
                            onClick={() => handleEdit(profile)}
                            disabled={loading || listLoading}
                            title="Edit Profile"
                          >
                            ✏️
                          </button>
                          <button
                            className="profile-delete-btn"
                            onClick={() => handleDelete(profile.userdata_id)}
                            disabled={loading || listLoading}
                            title="Delete Profile"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      
                      <div className="profile-item-details">
                        <div className="profile-info-grid">
                          <div className="profile-info-item">
                            <span className="info-label">📅 DOB:</span>
                            <span className="info-value">{formatDate(profile.dob)}</span>
                          </div>
                          <div className="profile-info-item">
                            <span className="info-label">⏰ TOB:</span>
                            <span className="info-value">{profile.tob || '-'}</span>
                          </div>
                          <div className="profile-info-item">
                            <span className="info-label">📍 POB:</span>
                            <span className="info-value">{profile.pob || '-'}</span>
                          </div>
                          <div className="profile-info-item">
                            <span className="info-label">📱 Mobile:</span>
                            <span className="info-value">{profile.mobile}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Profile Form Section */}
          <div className="profile-form-card">
            <div className="profile-form-header">
              <h3 className="profile-form-title">
                {formMode === "edit" ? (
                  <>
                    <span className="form-icon">✏️</span>
                    Edit Profile
                  </>
                ) : (
                  <>
                    <span className="form-icon">➕</span>
                    Create New Profile
                  </>
                )}
              </h3>
              {formMode === "edit" && (
                <button
                  className="cancel-edit-btn"
                  onClick={handleNewProfile}
                  disabled={loading}
                >
                  Cancel
                </button>
              )}
            </div>
            
            <form onSubmit={handleFormSubmit} className="modern-profile-form">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label className="form-label required">
                    <span className="label-icon">👤</span>
                    Full Name
                  </label>
                  <input
                    name="full_name"
                    value={form.full_name}
                    onChange={handleInput}
                    className="modern-profile-input"
                    placeholder="Enter full name"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">
                    <span className="label-icon">📅</span>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={form.dob}
                    onChange={handleInput}
                    className="modern-profile-input"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">
                    <span className="label-icon">⏰</span>
                    Time of Birth
                  </label>
                  <input
                    type="time"
                    name="birth_time"
                    value={form.birth_time}
                    onChange={handleInput}
                    className="modern-profile-input"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label required">
                    <span className="label-icon">📍</span>
                    Place of Birth
                  </label>
                  <PlaceOfBirthField
                    value={form.birth_place}
                    onChange={handlePobChange}
                    placeholder="Search for place, district, or state..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">
                    <span className="label-icon">⚧️</span>
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={form.gender}
                    onChange={handleInput}
                    className="modern-profile-select"
                    required
                    disabled={loading}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">♂️ Male</option>
                    <option value="Female">♀️ Female</option>
                    <option value="Other">⚧️ Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label required">
                    <span className="label-icon">📱</span>
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={form.mobile}
                    onChange={handleInput}
                    className="modern-profile-input"
                    placeholder="10-digit mobile number"
                    pattern="[6-9]\d{9}"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Messages */}
              {formError && (
                <div className="modern-message error-message">
                  <span className="message-icon">⚠️</span>
                  {formError}
                </div>
              )}
              {formSuccess && (
                <div className="modern-message success-message">
                  <span className="message-icon">✅</span>
                  {formSuccess}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="modern-profile-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="btn-spinner"></div>
                    {formMode === "edit" ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>
                    <span className="btn-icon">
                      {formMode === "edit" ? "💾" : "➕"}
                    </span>
                    {formMode === "edit" ? "Update Profile" : "Create Profile"}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileList;
